import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";

const LOG_FILES = [
  { key: "app", label: "App Log", desc: "All events" },
  { key: "trades", label: "Trades", desc: "Trade placed/closed/SL/target" },
  { key: "decisions", label: "Decisions", desc: "Signal taken/skipped per candle" },
];

const LEVELS = ["", "DEBUG", "INFO", "WARNING", "ERROR"];

const LEVEL_COLORS = {
  DEBUG: "#78909c",
  INFO: "#69f0ae",
  WARNING: "#ffb74d",
  ERROR: "#ff5252",
};

function getLevel(line) {
  for (const l of ["ERROR", "WARNING", "INFO", "DEBUG"]) {
    if (line.includes(` ${l} `)) return l;
  }
  return "";
}

export default function Logs() {
  const [activeFile, setActiveFile] = useState("app");
  const [lines, setLines] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState("5");
  const [maxLines, setMaxLines] = useState("200");
  const [levelFilter, setLevelFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [autoScroll, setAutoScroll] = useState(true);
  const logRef = useRef(null);
  const intervalRef = useRef(null);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { file: activeFile, lines: parseInt(maxLines) || 200 };
      if (levelFilter) params.level = levelFilter;
      if (searchTerm) params.search = searchTerm;
      const res = await axios.get("/api/logs", { params, timeout: 10000 });
      if (res.data.success) {
        setLines(res.data.lines || []);
        setTotal(res.data.total || 0);
      }
    } catch (err) {
      console.error("Failed to fetch logs:", err);
    }
    setLoading(false);
  }, [activeFile, maxLines, levelFilter, searchTerm]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Auto-scroll to bottom
  useEffect(() => {
    if (autoScroll && logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [lines, autoScroll]);

  // Auto-refresh
  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(fetchLogs, parseInt(refreshInterval) * 1000);
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [autoRefresh, refreshInterval, fetchLogs]);

  const clearLog = async () => {
    if (!window.confirm(`Clear ${activeFile}.log?`)) return;
    try {
      await axios.post(`/api/logs/clear?file=${activeFile}`);
      setLines([]);
      setTotal(0);
    } catch {}
  };

  const exportLog = () => {
    const content = lines.join("\n");
    const blob = new Blob([content], { type: "text/plain" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${activeFile}_${new Date().toISOString().slice(0, 10)}.log`;
    a.click();
  };

  return (
    <div className="five-ema">
      <div className="dashboard-header">
        <div>
          <h2>System Logs</h2>
          <p className="dashboard-subtitle">
            Real-time logs for signals, trades, and decisions
          </p>
        </div>
      </div>

      {/* Log file tabs */}
      <div className="ema-filters" style={{ marginBottom: 8, gap: 6 }}>
        {LOG_FILES.map(f => (
          <button key={f.key}
            className={`filter-chip ${activeFile === f.key ? "active" : ""}`}
            onClick={() => setActiveFile(f.key)}
            title={f.desc}
          >
            {f.label}
          </button>
        ))}
        <span style={{ margin: "0 8px", color: "#607d8b", fontSize: 12 }}>|</span>
        {LEVELS.map(l => (
          <button key={l || "all"}
            className={`filter-chip ${levelFilter === l ? "active" : ""}`}
            onClick={() => setLevelFilter(l)}
            style={l ? { borderColor: LEVEL_COLORS[l], color: LEVEL_COLORS[l] } : {}}
          >
            {l || "All Levels"}
          </button>
        ))}
      </div>

      {/* Controls */}
      <div className="dashboard-actions" style={{ marginBottom: 8, flexWrap: "wrap", gap: 6 }}>
        <input
          className="search-input"
          type="text"
          placeholder="Search logs..."
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") setSearchTerm(searchInput); }}
          style={{ maxWidth: 250 }}
        />
        <button className="btn btn-primary" onClick={() => setSearchTerm(searchInput)}>Search</button>
        {searchTerm && <button className="btn btn-secondary" onClick={() => { setSearchTerm(""); setSearchInput(""); }}>Clear</button>}
        <select className="ema-select" value={maxLines} onChange={e => setMaxLines(e.target.value)}>
          <option value="50">50 lines</option>
          <option value="100">100 lines</option>
          <option value="200">200 lines</option>
          <option value="500">500 lines</option>
          <option value="1000">1000 lines</option>
        </select>
        <button className="btn btn-primary" onClick={fetchLogs} disabled={loading}>
          {loading ? "Loading..." : "Refresh"}
        </button>
        <label className={`sim-toggle ${autoRefresh ? "sim-on" : "sim-off"}`} style={{ fontSize: 11 }}>
          <input type="checkbox" checked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)} />
          Auto {autoRefresh ? "ON" : "OFF"}
        </label>
        {autoRefresh && (
          <select className="ema-select" value={refreshInterval} onChange={e => setRefreshInterval(e.target.value)} style={{ width: 60 }}>
            <option value="2">2s</option>
            <option value="5">5s</option>
            <option value="10">10s</option>
            <option value="30">30s</option>
          </select>
        )}
        <label className={`sim-toggle ${autoScroll ? "sim-on" : "sim-off"}`} style={{ fontSize: 11 }}>
          <input type="checkbox" checked={autoScroll} onChange={e => setAutoScroll(e.target.checked)} />
          Scroll
        </label>
        <button className="btn btn-export" onClick={exportLog}>Export</button>
        <button className="btn btn-danger" onClick={clearLog} style={{ fontSize: 11 }}>Clear Log</button>
      </div>

      {/* Stats bar */}
      <div className="stats-bar" style={{ marginBottom: 8 }}>
        <div className="stat-card">
          <div className="label">File</div>
          <div className="value">{activeFile}.log</div>
        </div>
        <div className="stat-card">
          <div className="label">Total Lines</div>
          <div className="value">{total.toLocaleString()}</div>
        </div>
        <div className="stat-card">
          <div className="label">Showing</div>
          <div className="value">{lines.length}</div>
        </div>
        {levelFilter && (
          <div className="stat-card">
            <div className="label">Level</div>
            <div className="value" style={{ color: LEVEL_COLORS[levelFilter] }}>{levelFilter}</div>
          </div>
        )}
        {searchTerm && (
          <div className="stat-card">
            <div className="label">Search</div>
            <div className="value">"{searchTerm}"</div>
          </div>
        )}
      </div>

      {/* Log viewer */}
      <div
        ref={logRef}
        style={{
          background: "#0d1117",
          border: "1px solid #21262d",
          borderRadius: 8,
          padding: "12px 16px",
          fontFamily: "'JetBrains Mono', 'Fira Code', 'Cascadia Code', monospace",
          fontSize: 11.5,
          lineHeight: 1.6,
          maxHeight: "65vh",
          overflowY: "auto",
          whiteSpace: "pre-wrap",
          wordBreak: "break-all",
        }}
      >
        {lines.length === 0 ? (
          <div style={{ color: "#546e7a", textAlign: "center", padding: 40 }}>
            {loading ? "Loading logs..." : "No log entries found"}
          </div>
        ) : (
          lines.map((line, i) => {
            const level = getLevel(line);
            const color = LEVEL_COLORS[level] || "var(--text-dim)";
            const isSignal = line.includes("SIGNAL:") || line.includes("TRADE");
            return (
              <div
                key={i}
                style={{
                  color,
                  background: isSignal ? "#1a237e22" : "transparent",
                  borderLeft: isSignal ? "3px solid #448aff" : "3px solid transparent",
                  paddingLeft: 8,
                  marginLeft: -8,
                }}
              >
                {line}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
